import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BlogListHeader } from '@/components/blog/BlogPageHeaders';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { Toggle } from '@/components/ui/Toggle';
import { Tooltip } from '@/components/ui/Tooltip';
import { PlaceholderImage } from '@/components/ui/PlaceholderImage';
import { RetryImage } from '@/components/ui';
import { ICONS } from '@/constants/icons';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  useBlogPosts,
  useDeleteBlogPost,
  useBlogAutoGenerateSetting,
  useUpdateBlogAutoGenerateSetting,
} from '@/hooks/useBlog';
import { useWordPressChannels, useWordPressCategories, useWordPressTags } from '@/hooks/useWordPress';
import { SiteFavicon } from '@/components/blog/SiteFavicon';
import { BlogQuotaUsageLine } from '@/components/blog/BlogQuotaUsageLine';
import { ROW_ACTION_MENU_DIVIDER, ROW_ACTION_MENU_ITEM, ROW_ACTION_MENU_SURFACE } from '@/components/ui/rowActionMenuStyles';
import { BlogPost, BlogPostListItem, BlogPostStatus } from '@/services/blog.api';
import { toast } from '@/utils/toast';
import { formatApiError, isGlobalGatingHandledError } from '@/services/api';

/**
 * Blog List Page (SPEC.md Phase 1)
 *
 * Lists the team's blog posts pulled from Google Docs and published (or
 * scheduled/failed) to a connected WordPress site.
 *
 * Styled to match the creator PostTable benchmark (`components/creator/PostTable.tsx`):
 * `bg-white rounded-xl shadow-card` container, desktop table / mobile card
 * layout, `px-2 py-0.5 bg-gray-100 text-gray-600` meta badges, icon+label
 * status pills, a vertical-ellipsis actions dropdown with smart (viewport-aware)
 * positioning, and matching loading/empty/error states.
 */

/** Local status-pill config — icon + colour classes; label comes from i18n.
 *  Kept local to this page (not a shared component) per the design brief. */
const STATUS_PILL: Record<BlogPostStatus, { icon: string; color: string }> = {
  DRAFT: { icon: ICONS.DRAFT, color: 'bg-amber-100 text-amber-700' },
  SCHEDULED: { icon: ICONS.CLOCK, color: 'bg-soft-mint text-dark-teal' },
  PUBLISHED: { icon: ICONS.CHECK_CIRCLE, color: 'bg-green-100 text-green-700' },
  FAILED: { icon: ICONS.WARNING, color: 'bg-red-100 text-red-700' },
  // WP post was trashed/deleted directly on WordPress — distinct from FAILED
  // (our publish attempt failing) with a muted "no longer live" treatment.
  TRASHED: { icon: ICONS.RECYCLE_BIN, color: 'bg-gray-100 text-gray-500' },
};

/** Quick word count from the stored HTML body — good enough for a meta
 *  badge, not a full HTML-to-text parser. */
const countWords = (html: string): number => {
  const text = html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  return text ? text.split(/\s+/).filter(Boolean).length : 0;
};

/** The single timezone-aware date a row should surface, matched to status:
 *  published -> publishedAt, scheduled -> scheduledAt, else -> updatedAt. */
const dateFor = (post: BlogPostListItem): string => {
  if (post.status === 'PUBLISHED' && post.publishedAt) return post.publishedAt;
  if (post.status === 'SCHEDULED' && post.scheduledAt) return post.scheduledAt;
  return post.updatedAt;
};

type MenuPosition = { top?: number; bottom?: number; left: number };
const BLOG_ACTION_MENU_WIDTH_PX = 192;

type BlogTermsBadgesProps = {
  channelId: number | null;
  categoryIds: number[];
  tagIds: number[];
};

/** Category/tag count badges with a hover Tooltip resolving the actual
 *  term NAMES. The blog list endpoint only returns `categoryIds`/`tagIds`
 *  (numbers) — names live on the WordPress site and are fetched per-channel
 *  via the same `useWordPressCategories`/`useWordPressTags` hooks BlogDetail
 *  uses. Rendered once per row (never inside a loop) so each row's hook
 *  calls are unconditional per the rules of hooks; React Query dedupes by
 *  channelId, so many rows on the same WP site share one underlying fetch
 *  instead of firing an N-request storm. */
const BlogTermsBadges = ({ channelId, categoryIds, tagIds }: BlogTermsBadgesProps) => {
  const { t } = useTranslation('common');
  const { data: categories, isLoading: categoriesLoading } = useWordPressCategories(channelId ?? undefined);
  const { data: tags, isLoading: tagsLoading } = useWordPressTags(channelId ?? undefined);

  /** Builds the tooltip text for a set of ids: "No site" while there's no
   *  channel to resolve against, "Loading…" while the term list is still
   *  in flight, else the resolved names joined by commas (falling back to
   *  `#<id>` for any id the site no longer has). */
  const resolveTooltip = (
    ids: number[],
    terms: { id: number; name: string }[] | undefined,
    isLoading: boolean
  ): string => {
    if (!channelId) {
      return t('blog_terms_no_site', { defaultValue: 'No site selected — names unavailable' });
    }
    if (isLoading) {
      return t('loading', { defaultValue: 'Loading…' });
    }
    return ids.map((id) => terms?.find((term) => term.id === id)?.name ?? `#${id}`).join(', ');
  };

  const badgeClass = 'px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded flex items-center gap-1';

  return (
    <>
      {tagIds.length > 0 && (
        <Tooltip content={resolveTooltip(tagIds, tags, tagsLoading)}>
          <span className={badgeClass}>
            <Icon name={ICONS.TAG} size="xs" ariaHidden />
            {tagIds.length}
          </span>
        </Tooltip>
      )}
      {categoryIds.length > 0 && (
        <Tooltip content={resolveTooltip(categoryIds, categories, categoriesLoading)}>
          <span className={badgeClass}>
            <Icon name={ICONS.FOLDER} size="xs" ariaHidden />
            {categoryIds.length}
          </span>
        </Tooltip>
      )}
    </>
  );
};

export const Blog = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { formatDateTime, formatRelativeDate } = useTimezone();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<BlogPost | null>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);

  const { data, isLoading, error, refetch } = useBlogPosts({ page, limit: 20 });
  const deleteMutation = useDeleteBlogPost();

  // Phase 2 (SPEC.md decision C) — team-level "auto-generate on publish" toggle.
  const { data: autoGenerateSetting, isLoading: isLoadingAutoGenerate } = useBlogAutoGenerateSetting();
  const updateAutoGenerateMutation = useUpdateBlogAutoGenerateSetting();

  // Purely presentational join against the team's connected WordPress
  // channels so each row can show "published to <site>" — the blog list
  // endpoint itself only returns the raw channelId, no relation.
  const { data: wpChannels } = useWordPressChannels();
  const siteByChannelId = new Map((wpChannels ?? []).map((c) => [c.id, c.siteUrl.replace(/^https?:\/\//, '')]));
  const iconByChannelId = new Map((wpChannels ?? []).map((c) => [c.id, c.siteIconUrl]));

  const STATUS_LABEL: Record<BlogPostStatus, string> = {
    DRAFT: t('blog_status_draft', { defaultValue: 'Draft' }),
    SCHEDULED: t('blog_status_scheduled', { defaultValue: 'Scheduled' }),
    PUBLISHED: t('blog_status_published', { defaultValue: 'Published' }),
    FAILED: t('blog_status_failed', { defaultValue: 'Failed' }),
    TRASHED: t('blog_status_trashed', { defaultValue: 'Trashed' }),
  };

  const handleToggleAutoGenerate = (enabled: boolean) => {
    updateAutoGenerateMutation.mutate(enabled, {
      onError: (err) => {
        if (!isGlobalGatingHandledError(err)) toast.error(formatApiError(err).message);
      },
    });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success(t('blog_delete_success', { defaultValue: 'Blog post deleted' }));
      setDeleteTarget(null);
    } catch (err) {
      if (!isGlobalGatingHandledError(err)) toast.error(formatApiError(err).message);
    }
  };

  const closeMenu = () => {
    setOpenMenuId(null);
    setMenuPosition(null);
  };

  /** Anchors the row menu to its left-side trigger without crossing the viewport. */
  const openMenu = (e: React.MouseEvent<HTMLButtonElement>, postId: number) => {
    if (openMenuId === postId) {
      closeMenu();
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const menuWidth = Math.min(BLOG_ACTION_MENU_WIDTH_PX, window.innerWidth - 16);
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - menuWidth - 8);
    if (spaceBelow >= 180) {
      setMenuPosition({ top: rect.bottom + 4, left });
    } else {
      setMenuPosition({ bottom: window.innerHeight - rect.top + 4, left });
    }
    setOpenMenuId(postId);
  };

  const renderMenuItems = (post: BlogPostListItem) => (
    <>
      <button
        onClick={() => { closeMenu(); navigate(`/blog/${post.id}`); }}
        className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
      >
        <Icon name={ICONS.EYE} size="sm" ariaHidden />
        {t('blog_action_open', { defaultValue: 'Open' })}
      </button>
      {post.wpPostUrl && post.status === 'PUBLISHED' && (
        <a
          href={post.wpPostUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeMenu}
          className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
        >
          <Icon name={ICONS.EXTERNAL_LINK} size="sm" ariaHidden />
          {t('blog_view_on_wordpress', { defaultValue: 'View on WordPress' })}
        </a>
      )}
      {post.wpPostUrl && post.status !== 'PUBLISHED' && (
        <Tooltip
          content={post.status === 'SCHEDULED'
            ? t('blog_wp_scheduled_hint', { defaultValue: 'Viewable once it goes live' })
            : post.status === 'TRASHED'
              ? t('blog_wp_trashed_hint', { defaultValue: 'Removed from WordPress' })
              : t('blog_wp_draft_hint', { defaultValue: 'Publish to view on WordPress' })}
        >
          <span className="block min-w-0 truncate px-[6px] py-[6px] text-xs leading-[20px] text-gray-500">
            {post.status === 'SCHEDULED'
              ? t('blog_wp_scheduled_hint', { defaultValue: 'Viewable once it goes live' })
              : post.status === 'TRASHED'
                ? t('blog_wp_trashed_hint', { defaultValue: 'Removed from WordPress' })
                : t('blog_wp_draft_hint', { defaultValue: 'Publish to view on WordPress' })}
          </span>
        </Tooltip>
      )}
      <div className={ROW_ACTION_MENU_DIVIDER} />
      <button
        onClick={() => { closeMenu(); setDeleteTarget(post); }}
        className={ROW_ACTION_MENU_ITEM + ' text-red-600 hover:bg-red-50 focus-visible:bg-red-50'}
      >
        <Icon name={ICONS.TRASH} size="sm" ariaHidden />
        {t('delete', { defaultValue: 'Delete' })}
      </button>
    </>
  );

  return (
    <div>
      <BlogListHeader />

      <div className="space-y-4">
        <BlogQuotaUsageLine className="justify-end" />
        {/* Toolbar — auto-generate setting and primary action */}
        <div className="flex flex-wrap items-center justify-end gap-3">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* Phase 2 (SPEC.md decision C) — team-level auto-generate setting,
                folded into a compact pill affordance. */}
            <Tooltip
              content={t('blog_auto_generate_subtitle', {
                defaultValue: 'Skip the manual prompt — social drafts are created automatically whenever you push a blog to WordPress, including drafts.',
              })}
              side="bottom"
            >
              <div className="flex items-center gap-2 pl-3 pr-2.5 py-1.5 rounded-full border border-gray-200 bg-white shadow-sm">
                <Icon name={ICONS.MAGIC_WAND} size="xs" color="text-gray-400" ariaHidden />
                <span className="text-xs font-medium text-gray-600 hidden sm:inline whitespace-nowrap">
                  {t('blog_auto_generate_title_short', { defaultValue: 'Auto-generate social' })}
                </span>
                {isLoadingAutoGenerate ? (
                  <div className="h-5 w-9 flex-shrink-0 rounded-full bg-gray-100 animate-pulse" />
                ) : (
                  <Toggle
                    ariaLabel={t('blog_auto_generate_title_short', { defaultValue: 'Auto-generate social' })}
                    checked={autoGenerateSetting?.autoGenerateSocialOnBlogPublish ?? false}
                    onChange={handleToggleAutoGenerate}
                  />
                )}
              </div>
            </Tooltip>

            <Button
              variant="primary"
              onClick={() => navigate('/blog/new')}
              className="flex items-center gap-2"
            >
              <Icon name={ICONS.PLUS} size="sm" ariaHidden />
              {t('blog_new_button', { defaultValue: 'New Blog Post' })}
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-card">
          {isLoading ? (
            // Loading skeleton — table layout mirroring the actual table structure (PostTable convention)
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="w-12 px-2 py-3" aria-label={t('blog_actions', { defaultValue: 'Post actions' })}></th>
                    <th className="p-3 text-left text-xs font-semibold text-gray-600">{t('blog_col_post', { defaultValue: 'Post' })}</th>
                    <th className="hidden lg:table-cell w-40 p-3 text-left text-xs font-semibold text-gray-600">{t('blog_col_site', { defaultValue: 'Site' })}</th>
                    <th className="hidden md:table-cell w-32 p-3 text-left text-xs font-semibold text-gray-600">{t('status', { defaultValue: 'Status' })}</th>
                    <th className="hidden md:table-cell w-36 p-3 text-left text-xs font-semibold text-gray-600">{t('blog_col_date', { defaultValue: 'Date' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="w-12 px-2 py-3 align-top"><div className="flex h-14 items-center justify-center"><div className="h-7 w-7 rounded-lg bg-gray-200 animate-pulse" /></div></td>
                      <td className="p-3 align-top">
                        <div className="flex gap-3">
                          <div className="w-14 h-14 bg-gray-200 rounded-lg animate-pulse flex-shrink-0" />
                          <div className="flex-1 space-y-2 py-1">
                            <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse" />
                            <div className="h-3 bg-gray-200 rounded w-1/3 animate-pulse" />
                          </div>
                        </div>
                      </td>
                      <td className="hidden lg:table-cell p-3 align-top"><div className="h-4 bg-gray-200 rounded w-24 animate-pulse" /></td>
                      <td className="hidden md:table-cell p-3 align-top"><div className="h-6 bg-gray-200 rounded-full w-20 animate-pulse" /></td>
                      <td className="hidden md:table-cell p-3 align-top"><div className="h-4 bg-gray-200 rounded w-20 animate-pulse" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : error ? (
            <div className="py-16 text-center px-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-4">
                <Icon name={ICONS.WARNING} size="lg" color="text-red-500" ariaHidden />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {t('blog_load_error', { defaultValue: 'Failed to load blog posts' })}
              </h3>
              <p className="text-sm text-gray-500 mb-4">{formatApiError(error).message}</p>
              <Button variant="secondary" size="sm" onClick={() => refetch()}>
                {t('blog_try_again', { defaultValue: 'Try Again' })}
              </Button>
            </div>
          ) : data && data.items.length > 0 ? (
            <>
              {isMobile ? (
                // Mobile card view — mirrors PostTable's mobile card structure
                <div className="grid grid-cols-1 gap-4 p-4">
                  {data.items.map((post) => {
                    const site = post.channelId !== null ? siteByChannelId.get(post.channelId) : undefined;
                    const words = countWords(post.htmlBody);
                    const pill = STATUS_PILL[post.status];

                    return (
                      <div
                        key={post.id}
                        onClick={() => navigate(`/blog/${post.id}`)}
                        className="border border-gray-200 rounded-lg p-4 space-y-3 cursor-pointer hover:border-gray-300 transition-colors"
                      >
                        {/* Row 1: actions + status on the left, date on the right */}
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex min-w-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Tooltip content={t('actions', { defaultValue: 'Actions' })}>
                              <button
                                type="button"
                                onClick={(e) => openMenu(e, post.id)}
                                className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-soft-mint transition-colors hover:bg-soft-mint/70 after:absolute after:-inset-[8px] after:content-['']"
                                aria-label={t('blog_actions', { defaultValue: 'Post actions' })}
                                aria-expanded={openMenuId === post.id}
                              >
                                <Icon name={ICONS.ELLIPSIS_VERTICAL} size="sm" color="text-dark-teal" ariaHidden />
                              </button>
                            </Tooltip>
                            <span className={`inline-flex min-w-0 items-center gap-1 truncate rounded-full px-2 py-0.5 text-xs font-medium ${pill.color}`}>
                              <Icon name={pill.icon} size="xs" ariaHidden />
                              {STATUS_LABEL[post.status]}
                            </span>
                            {openMenuId === post.id && menuPosition && (
                              <>
                                <div className="fixed inset-0 z-40" onClick={closeMenu} />
                                <div
                                  className={`fixed z-50 max-h-[calc(100dvh-1rem)] overflow-y-auto ${ROW_ACTION_MENU_SURFACE}`}
                                  style={{
                                    width: BLOG_ACTION_MENU_WIDTH_PX,
                                    left: menuPosition.left,
                                    ...(menuPosition.top !== undefined ? { top: menuPosition.top } : { bottom: menuPosition.bottom }),
                                  }}
                                >
                                  {renderMenuItems(post)}
                                </div>
                              </>
                            )}
                          </div>
                          <Tooltip content={formatDateTime(dateFor(post))}>
                            <span className="shrink-0 whitespace-nowrap text-xs text-gray-400">{formatRelativeDate(dateFor(post))}</span>
                          </Tooltip>
                        </div>

                        {/* Row 2: thumbnail (1/3) + title/excerpt (2/3) */}
                        <div className="flex gap-3 items-start">
                          <div className="relative w-1/3 flex-shrink-0 aspect-square bg-gray-100 rounded-lg overflow-hidden">
                            {post.featuredMedia?.url ? (
                              <RetryImage src={post.featuredMedia.url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <PlaceholderImage />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 line-clamp-2">{post.title}</p>
                            {post.excerpt && (
                              <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{post.excerpt}</p>
                            )}
                            {post.author && (
                              <p className="text-xs text-gray-400 mt-0.5 truncate">
                                {t('blog_by_author', { defaultValue: `by ${post.author.fullName}`, name: post.author.fullName })}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Row 3: meta badges */}
                        <div className="flex flex-wrap items-center gap-1.5 text-xs">
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 font-medium rounded">
                            {t('blog_word_count', { defaultValue: `${words} words`, count: words })}
                          </span>
                          {post.imageCount > 0 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 font-medium rounded flex items-center gap-1">
                              <Icon name={ICONS.IMAGE} size="xs" ariaHidden />
                              {post.imageCount}
                            </span>
                          )}
                          {post.socialDraftsCount > 0 && (
                            <Tooltip
                              content={t('blog_social_drafts_tooltip', {
                                defaultValue: `${post.socialDraftsCount} social draft${post.socialDraftsCount === 1 ? '' : 's'} generated`,
                                count: post.socialDraftsCount,
                              })}
                            >
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); navigate('/calendar'); }}
                                className="px-2 py-0.5 bg-soft-mint text-dark-teal font-medium rounded flex items-center gap-1"
                              >
                                <Icon name={ICONS.MAGIC_WAND} size="xs" ariaHidden />
                                {t('blog_social_drafts_count', { defaultValue: `${post.socialDraftsCount} drafts`, count: post.socialDraftsCount })}
                              </button>
                            </Tooltip>
                          )}
                          <BlogTermsBadges
                            channelId={post.channelId}
                            categoryIds={post.categoryIds}
                            tagIds={post.tagIds}
                          />
                          {site && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 font-medium rounded flex items-center gap-1">
                              <SiteFavicon iconUrl={post.channelId !== null ? iconByChannelId.get(post.channelId) : undefined} siteName={site} size="xs" />
                              {site}
                            </span>
                          )}
                          {post.status === 'DRAFT' && !post.channelId && (
                            <span className="px-2 py-0.5 bg-amber-50 text-amber-700 font-medium rounded flex items-center gap-1">
                              <Icon name={ICONS.WARNING} size="xs" ariaHidden />
                              {t('blog_no_site_selected', { defaultValue: 'No site selected' })}
                            </span>
                          )}
                          {post.status === 'FAILED' && post.lastError && (
                            <Tooltip content={post.lastError}>
                              <span className="px-2 py-0.5 bg-red-50 text-red-600 font-medium rounded truncate max-w-[180px]">
                                {post.lastError}
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                // Desktop table view
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="w-12 px-2 py-3" aria-label={t('blog_actions', { defaultValue: 'Post actions' })}></th>
                        <th className="p-3 text-left text-xs font-semibold text-gray-600">{t('blog_col_post', { defaultValue: 'Post' })}</th>
                        <th className="hidden lg:table-cell w-40 p-3 text-left text-xs font-semibold text-gray-600">{t('blog_col_site', { defaultValue: 'Site' })}</th>
                        <th className="hidden md:table-cell w-32 p-3 text-left text-xs font-semibold text-gray-600">{t('status', { defaultValue: 'Status' })}</th>
                        <th className="hidden md:table-cell w-36 p-3 text-left text-xs font-semibold text-gray-600">{t('blog_col_date', { defaultValue: 'Date' })}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((post) => {
                        const site = post.channelId !== null ? siteByChannelId.get(post.channelId) : undefined;
                        const words = countWords(post.htmlBody);
                        const pill = STATUS_PILL[post.status];

                        return (
                          <tr
                            key={post.id}
                            onClick={() => navigate(`/blog/${post.id}`)}
                            className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <td className="w-12 px-2 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                              <div className="flex h-14 items-center justify-center">
                                <Tooltip content={t('actions', { defaultValue: 'Actions' })}>
                                  <button
                                    type="button"
                                    onClick={(e) => openMenu(e, post.id)}
                                    className="rounded-lg p-1.5 transition-colors hover:bg-gray-100"
                                    aria-label={t('blog_actions', { defaultValue: 'Post actions' })}
                                    aria-expanded={openMenuId === post.id}
                                  >
                                    <Icon name={ICONS.ELLIPSIS_VERTICAL} size="sm" color="text-gray-600" ariaHidden />
                                  </button>
                                </Tooltip>
                                {openMenuId === post.id && menuPosition && (
                                  <>
                                    <div className="fixed inset-0 z-40" onClick={closeMenu} />
                                    <div
                                      className={`fixed z-50 max-h-[calc(100dvh-1rem)] overflow-y-auto ${ROW_ACTION_MENU_SURFACE}`}
                                      style={{
                                        width: BLOG_ACTION_MENU_WIDTH_PX,
                                        left: menuPosition.left,
                                        ...(menuPosition.top !== undefined ? { top: menuPosition.top } : { bottom: menuPosition.bottom }),
                                      }}
                                    >
                                      {renderMenuItems(post)}
                                    </div>
                                  </>
                                )}
                              </div>
                            </td>
                            {/* Thumbnail + title/meta */}
                            <td className="p-3 align-top">
                              <div className="flex gap-3">
                                <div className="relative w-14 h-14 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 group">
                                  {post.featuredMedia?.url ? (
                                    <>
                                      <RetryImage src={post.featuredMedia.url} alt="" className="w-full h-full object-cover" />
                                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                                    </>
                                  ) : (
                                    <PlaceholderImage />
                                  )}
                                </div>
                                <div className="min-w-0 flex-1 space-y-1">
                                  <p className="font-semibold text-gray-900 line-clamp-2 text-sm">{post.title}</p>
                                  {post.excerpt && (
                                    <p className="text-xs text-gray-500 line-clamp-1">{post.excerpt}</p>
                                  )}
                                  {post.author && (
                                    <p className="text-xs text-gray-400 truncate">
                                      {t('blog_by_author', { defaultValue: `by ${post.author.fullName}`, name: post.author.fullName })}
                                    </p>
                                  )}
                                  <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                                      {t('blog_word_count', { defaultValue: `${words} words`, count: words })}
                                    </span>
                                    {post.imageCount > 0 && (
                                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded flex items-center gap-1">
                                        <Icon name={ICONS.IMAGE} size="xs" ariaHidden />
                                        {post.imageCount}
                                      </span>
                                    )}
                                    {post.socialDraftsCount > 0 && (
                                      <Tooltip
                                        content={t('blog_social_drafts_tooltip', {
                                          defaultValue: `${post.socialDraftsCount} social draft${post.socialDraftsCount === 1 ? '' : 's'} generated`,
                                          count: post.socialDraftsCount,
                                        })}
                                      >
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); navigate('/calendar'); }}
                                          className="px-2 py-0.5 bg-soft-mint text-dark-teal text-xs font-medium rounded flex items-center gap-1"
                                        >
                                          <Icon name={ICONS.MAGIC_WAND} size="xs" ariaHidden />
                                          {t('blog_social_drafts_count', { defaultValue: `${post.socialDraftsCount} drafts`, count: post.socialDraftsCount })}
                                        </button>
                                      </Tooltip>
                                    )}
                                    <BlogTermsBadges
                                      channelId={post.channelId}
                                      categoryIds={post.categoryIds}
                                      tagIds={post.tagIds}
                                    />
                                    {post.status === 'DRAFT' && !post.channelId && (
                                      <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs font-medium rounded flex items-center gap-1">
                                        <Icon name={ICONS.WARNING} size="xs" ariaHidden />
                                        {t('blog_no_site_selected', { defaultValue: 'No site selected' })}
                                      </span>
                                    )}
                                    {post.status === 'FAILED' && post.lastError && (
                                      <Tooltip content={post.lastError}>
                                        <span className="px-2 py-0.5 bg-red-50 text-red-600 text-xs font-medium rounded truncate max-w-[220px] inline-block align-bottom">
                                          {post.lastError}
                                        </span>
                                      </Tooltip>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Target WordPress site */}
                            <td className="hidden lg:table-cell p-3 align-top">
                              {site ? (
                                <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                  <SiteFavicon iconUrl={post.channelId !== null ? iconByChannelId.get(post.channelId) : undefined} siteName={site} size="xs" />
                                  {site}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-300">—</span>
                              )}
                            </td>

                            {/* Status pill */}
                            <td className="hidden md:table-cell p-3 align-top">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${pill.color}`}>
                                <Icon name={pill.icon} size="xs" ariaHidden />
                                {STATUS_LABEL[post.status]}
                              </span>
                            </td>

                            {/* Date — timezone-aware, absolute on hover */}
                            <td className="hidden md:table-cell p-3 align-top">
                              <Tooltip content={formatDateTime(dateFor(post))}>
                                <span className="text-xs text-gray-500 whitespace-nowrap">{formatRelativeDate(dateFor(post))}</span>
                              </Tooltip>
                            </td>

                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {data.pages > 1 && (
                <div className="px-4 sm:px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    {t('blog_showing_count', {
                      defaultValue: `${data.items.length} of ${data.total}`,
                      count: data.items.length,
                      total: data.total,
                    })}
                  </div>
                  <Pagination page={data.page} totalPages={data.pages} onPageChange={setPage} />
                </div>
              )}
            </>
          ) : (
            <EmptyState
              icon={ICONS.BLOG}
              title={t('blog_empty_title', { defaultValue: 'No blog posts yet' })}
              description={t('blog_empty_description', {
                defaultValue: 'Pull a Google Doc to draft your first blog post and publish it to WordPress.',
              })}
              action={
                <Button variant="primary" size="sm" onClick={() => navigate('/blog/new')}>
                  <Icon name={ICONS.PLUS} size="sm" className="mr-1.5" ariaHidden />
                  {t('blog_new_button', { defaultValue: 'New Blog Post' })}
                </Button>
              }
            />
          )}
        </div>
      </div>

      <ConfirmationModal
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('blog_delete_title', { defaultValue: 'Delete Blog Post' })}
        description={t('blog_delete_confirm', {
          defaultValue: `Are you sure you want to delete "${deleteTarget?.title}"? This will not remove it from WordPress if already published.`,
          title: deleteTarget?.title,
        })}
        confirmText={t('delete', { defaultValue: 'Delete' })}
        variant="danger"
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

export default Blog;
